import React, { Suspense } from "react";
import { useInView } from "react-intersection-observer";
import * as styles from "./MarkedTitle.module.scss";

type Variant = "program" | "topics" | "speakers";
type AnimationType = "marker" | "underline" | "overline" | "highlight";

type Props = {
    children: React.ReactNode;
    markerAnimationData?: unknown;
    variant?: Variant;
    animationType?: AnimationType;
    className?: string;
    markerClassName?: string;
    threshold?: number;
    triggerOnce?: boolean;
};

const cx = (...classes: Array<string | undefined | false>) =>
    classes.filter(Boolean).join(" ");

// Lottie-bearing variants are loaded lazily so `lottie-react` (~40KB gzip)
// doesn't ship to pages that only use `animationType="highlight"`.
const MarkerVariant = React.lazy(() => import("./MarkerVariant"));

const HighlightVariant: React.FC<Props> = ({
    children,
    className,
    threshold = 0.2,
    triggerOnce = true,
}) => {
    const { ref, inView } = useInView({ threshold, triggerOnce });
    return (
        <span
            ref={ref}
            className={cx(styles.highlight, inView && styles.highlightVisible, className)}
        >
            {children}
        </span>
    );
};

export const MarkedTitle: React.FC<Props> = (props) => {
    const { children, animationType = "marker", className } = props;

    if (animationType === "highlight") {
        return <HighlightVariant {...props} />;
    }

    return (
        <Suspense fallback={<span className={cx(styles.root, className)}>{children}</span>}>
            <MarkerVariant {...props} />
        </Suspense>
    );
};
